import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { LogOut, Shield } from 'lucide-react';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

function NavBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [environment, setEnvironment] = useState('production');

  useEffect(() => {
    fetch('/api/config')
      .then(res => res.json())
      .then(data => setEnvironment(data.environment || 'production'))
      .catch(() => {});
  }, []);

  const isDev = environment !== 'production';

  if (!user) return null;

  return (
    <nav className={`fixed top-0 left-0 right-0 h-14 bg-gradient-to-r ${isDev ? 'from-amber-700 to-orange-700' : 'from-slate-700 to-slate-800'} text-white z-50 shadow-lg`}>
      <div className="h-full px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <Shield size={18} />
          </div>
          <span className="font-semibold text-lg">Platform Admin</span>
          {isDev && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full uppercase tracking-wide">
              DEV
            </span>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            {user.picture && (
              <img
                src={user.picture}
                alt={user.name}
                className="w-8 h-8 rounded-full"
                referrerPolicy="no-referrer"
              />
            )}
            <div className="text-sm">
              <div className="font-medium">{user.name}</div>
              <div className="text-white/60 text-xs">{user.email}</div>
            </div>
          </div>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/10 transition text-sm text-white/80 hover:text-white"
          >
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </nav>
  );
}

function AppContent() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <>
      <NavBar />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={user ? (
            <div className="min-h-screen bg-gray-50 pt-14">
              <DashboardPage />
            </div>
          ) : (
            <Navigate to="/login" replace />
          )}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
